import { useEffect, useState } from 'react';
import { useRoleManagement, SortField } from '../hooks/useRoleManagement';
import { UserRole } from '../../../shared/types';
import { useAuth } from '../hooks/useAuth';
import CustomPagination from '../components/CustomPagination';
import UserInitials from '../components/UserInitials';
import CustomDropdown, { DropdownOption } from '../components/CustomDropdown';

export default function RoleManagement() {
  const { user } = useAuth();
  const { 
    users, 
    loading, 
    error, 
    pagination, 
    sortField, 
    sortDirection,
    fetchUsers, 
    updateUserRole, 
    setSort 
  } = useRoleManagement();
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [selectedUserFilter, setSelectedUserFilter] = useState<string[]>([]);

  // Create role options for the dropdown
  const roleOptions: DropdownOption[] = [
    { value: UserRole.USER, label: 'User' },
    { value: UserRole.ADMIN, label: 'Admin' },
    { value: UserRole.READER, label: 'Reader' }
  ];

  // Create user options for the search dropdown
  const userOptions: DropdownOption[] = users.map(userItem => ({
    value: userItem.id,
    label: `${userItem.name} (${userItem.email})`
  }));

  // Filter users based on selected user filter
  const filteredUsers = selectedUserFilter.length > 0
    ? users.filter(userItem => selectedUserFilter.includes(userItem.id))
    : users;

  useEffect(() => {
    fetchUsers(1, 50);
  }, [fetchUsers, sortField, sortDirection]);

  const handlePageChange = (page: number) => {
    fetchUsers(page, 50);
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      setUpdatingUserId(userId);
      await updateUserRole(userId, newRole);
    } catch (err) {
      console.error('Failed to update role:', err);
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleSort = (field: SortField) => {
    setSort(field);
    // The useEffect will automatically refetch data with new sort parameters
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'bg-red-100 text-red-800';
      case UserRole.USER:
        return 'bg-blue-100 text-blue-800';
      case UserRole.READER:
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'Admin';
      case UserRole.USER:
        return 'User';
      case UserRole.READER:
        return 'Reader';
      default:
        return role;
    }
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-left font-medium text-gray-900 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-2 py-1"
    >
      <span>{children}</span>
      {sortField === field && (
        <span className="text-gray-400">
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </button>
  );

  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Access Denied
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>You don't have permission to access this page. Only administrators can manage user roles.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Search Users:</span>
              <CustomDropdown
                id="user-search"
                label=""
                placeholder="Search by name or email..."
                options={userOptions}
                selectedValues={selectedUserFilter}
                onSelectionChange={setSelectedUserFilter}
                multiSelect={true}
                searchable={true}
                searchPlaceholder="Search by name or email..."
                showClearAll={true}
                clearAllText="Clear Filter"
                minWidth="min-w-[500px]"
                transparency="medium"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <CustomPagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
              onPageChange={handlePageChange}
              className="mb-0"
              transparency="medium"
              minWidth="min-w-[400px]"
            />
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !users.length ? (
        <div className="flex justify-center items-center py-12">
          <div data-testid="loading-spinner" className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-12 items-center">
              <div className="col-span-3">
                <SortableHeader field="name">
                  <span className="text-sm font-normal text-gray-900">Name</span>
                </SortableHeader>
              </div>
              <div className="col-span-4">
                <SortableHeader field="email">
                  <span className="text-sm font-normal text-gray-900">Email</span>
                </SortableHeader>
              </div>
              <div className="col-span-3">
                <SortableHeader field="role">
                  <span className="text-sm font-normal text-gray-900">Role</span>
                </SortableHeader>
              </div>
              <div className="col-span-2">
                <span className="text-sm font-normal text-gray-900">Actions</span>
              </div>
            </div>
          </div>
          <ul className="divide-y divide-gray-200">
            {filteredUsers.map((userItem) => (
              <li key={userItem.id} className="px-6 py-4">
                <div className="grid grid-cols-12 items-center">
                  <div className="col-span-3 flex items-center">
                    <UserInitials name={userItem.name} className="mr-3" />
                    <span className="text-sm font-medium text-gray-900">{userItem.name}</span>
                  </div>
                  <div className="col-span-4">
                    <span className="text-sm text-gray-500">{userItem.email}</span>
                  </div>
                  <div className="col-span-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(userItem.role)}`}>
                      {getRoleDisplayName(userItem.role)}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center justify-end space-x-4">
                    {userItem.id !== user.id ? (
                      <CustomDropdown
                        id={`role-${userItem.id}`}
                        options={roleOptions}
                        selectedValues={[userItem.role]}
                        onSelectionChange={(values) => {
                          if (values.length > 0) {
                            handleRoleChange(userItem.id, values[0] as UserRole);
                          }
                        }}
                        multiSelect={false}
                        disabled={updatingUserId === userItem.id}
                        searchable={true}
                        searchPlaceholder="Search roles..."
                        minWidth="min-w-[120px]"
                        transparency="medium"
                      />
                    ) : (
                      <div className="ml-2 w-8 h-8"></div>
                    )}
                    {updatingUserId === userItem.id && (
                      <div className="ml-2">
                        <div data-testid="role-update-spinner" className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
} 